import React from "react";
import { Check, Minus, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

/** Only the capabilities `to` adds over `from`, grouped as the pricing matrix groups them. */
export const planGaps = (matrix, fromKey, toKey) =>
  (matrix || [])
    .map((group) => ({
      group: group.group,
      rows: (group.rows || []).filter((r) => !r.values?.[fromKey] && r.values?.[toKey]),
    }))
    .filter((group) => group.rows.length > 0);

/**
 * The upgrade decision at the moment it is made, instead of a full matrix further down the page.
 * Derived from the catalogue the server sends, so it can never drift from real entitlements.
 */
export const PlanGapDialog = ({ matrix, from, to, onUpgrade, dark = false }) => {
  const groups = planGaps(matrix, from.key, to.key);
  const count = groups.reduce((n, g) => n + g.rows.length, 0);
  if (!count) return null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`plan-gap-open-${from.key}`}
          className={`mt-3 self-start text-xs font-semibold underline decoration-dotted underline-offset-4 transition-colors ${
            dark ? "text-slate-300 hover:text-white" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          See the {count} things {from.name} misses
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="plan-gap-dialog"
        className="max-h-[85vh] overflow-y-auto sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle data-testid="plan-gap-title">
            {from.name} vs {to.name}
          </DialogTitle>
          <DialogDescription>
            The {count} capabilities {to.name} adds. Everything else is identical, and you can move
            up at any time without losing anything you have already built.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <div className="flex items-end justify-end gap-4 border-b border-slate-200 pb-2">
            {[from.name, to.name].map((label) => (
              <span
                key={label}
                className="w-16 shrink-0 whitespace-nowrap text-center text-[8px] font-bold uppercase leading-tight tracking-normal text-slate-500 sm:w-20 sm:text-[10px]"
              >
                {label}
              </span>
            ))}
          </div>
          {groups.map((group) => (
            <div key={group.group} data-testid={`plan-gap-group-${group.group}`}>
              <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                {group.group}
              </p>
              {group.rows.map((row) => (
                <div
                  key={row.key}
                  data-testid={`plan-gap-row-${row.key}`}
                  className="flex items-center justify-between gap-4 border-b border-slate-100 py-2.5"
                >
                  <span className="text-sm text-slate-700">{row.label}</span>
                  <span className="flex shrink-0 items-center gap-4">
                    <span className="flex w-16 justify-center sm:w-20">
                      <Minus size={15} strokeWidth={1.5} className="text-slate-300" aria-label="Not included" />
                    </span>
                    <span className="flex w-16 justify-center sm:w-20">
                      <Check size={15} strokeWidth={2.5} className="text-emerald-600" aria-label="Included" />
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {onUpgrade && (
          <button
            type="button"
            data-testid="plan-gap-upgrade"
            onClick={onUpgrade}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-sm bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
          >
            Choose {to.name} instead <ArrowRight size={15} strokeWidth={1.5} />
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
